/**
 * Hooks for in-app calls (TRUS-668): config gate, the ringing list, a call's
 * live state. The session (Twilio) lives in useCallSession.ts, loaded lazily
 * with the call panel so twilio-video stays out of the main bundle.
 */

import { createContext, useContext, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import useActiveOrgId from '@/hooks/useActiveOrgId';
import {
  callsApi,
  CallsApi,
  DISABLED_CALLS_CONFIG,
  isNotesPending,
  type Call,
  type CallsConfig,
} from '@/services/callsApi';
import { useCallStore } from './callStore';

/** Staff screens use the default; the member portal provides memberCallsApi. */
export const CallsApiContext = createContext<CallsApi>(callsApi);
export const useCallsApi = () => useContext(CallsApiContext);

export const RINGING_POLL_MS = 4000;
/**
 * The ringing poll in a background tab / hidden or minimized window. It must
 * keep running there — that is exactly when a desktop notification is needed.
 * Browsers throttle hidden-tab timers (Chrome: about once a minute after ~5
 * minutes hidden); Web Push, arriving in a separate PR, covers that case.
 */
export const RINGING_POLL_HIDDEN_MS = 6000;

export function useCallsConfig(): { config: CallsConfig; isLoading: boolean } {
  const api = useCallsApi();
  const orgId = useActiveOrgId();
  const q = useQuery({
    queryKey: ['calls-config', api.kind, orgId],
    queryFn: () => api.getConfig(),
    staleTime: 60_000,
    retry: false,
  });
  return { config: q.data ?? DISABLED_CALLS_CONFIG, isLoading: q.isLoading };
}

export function useDocumentVisible(): boolean {
  const [visible, setVisible] = useState(() => typeof document === 'undefined' || !document.hidden);
  useEffect(() => {
    const onChange = () => setVisible(!document.hidden);
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return visible;
}

/**
 * Calls ringing for this user: GET /active (no channel) every 4 s while the
 * tab is visible, every 6 s while it is hidden (so a call in the background
 * still raises a desktop notification), whenever calls are enabled. Declined /
 * joined calls drop out.
 */
export function useIncomingCalls(enabled: boolean): Call[] {
  const api = useCallsApi();
  const orgId = useActiveOrgId();
  const visible = useDocumentVisible();
  const { session, dismissed } = useCallStore();
  const q = useQuery({
    queryKey: ['calls-ringing', api.kind, orgId],
    queryFn: async () => {
      try {
        return await api.active();
      } catch {
        return [] as Call[];
      }
    },
    enabled,
    refetchInterval: enabled ? (visible ? RINGING_POLL_MS : RINGING_POLL_HIDDEN_MS) : false,
    refetchIntervalInBackground: true,
    retry: false,
  });
  if (!enabled) return [];
  return (q.data ?? []).filter(
    (c) => c.status === 'active' && !c.is_participant && !dismissed.has(c.id) && session?.callId !== c.id,
  );
}

/** A call's live state (the message card / header use it over stale metadata). */
export function useLiveCall(callId: number | null | undefined, enabled = true) {
  const api = useCallsApi();
  return useQuery({
    queryKey: ['call', api.kind, callId],
    queryFn: () => api.get(callId as number),
    enabled: enabled && callId != null,
    staleTime: 10_000,
    // Keep polling an ended call only while its notes are being prepared (v2).
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.status === 'ended' && !isNotesPending(data.notes_status)) return false;
      return 15_000;
    },
    retry: false,
  });
}

